import React, { useEffect, useCallback } from 'react';
import SatButton from '@components/common/satUI/SatButton';
import { isMobile } from '@common/utils/device';

const BW = 5; // 보정값

export default function (props) {
  const {
    canvasRef,
    title,
    error,
    name,
    pasteData,
    pasteCheck,
    pasteCheck2,
    signData,
    signData2,
    onChange,
    onChangePasteCheck,
    onChangePasteCheck2,
  } = props;

  let canvas, context;
  let isPainting = false;
  let mousePosition = {};

  useEffect(() => {
    if (pasteCheck) {
      // 붙여넣기 체크한 경우
      clearCanvas();
      fromDataURL(pasteData);
    }
  }, [pasteCheck, pasteData]);
  
  useEffect(() => {
    if (pasteCheck2) {
      // 붙여넣기 체크한 경우
      clearCanvas();
      fromDataURL(pasteData);
    }
  }, [pasteCheck2, pasteData]);
  
  useEffect(() => {
    if (signData) {
      fromDataURL(signData);
    }
  }, [signData]);

  useEffect(() => {
    if (signData2) {
      fromDataURL(signData2);
    }
  }, [signData2]);

  // 태블릿에서 Sign 하는 중 스크롤 되어 흔들리는 현상 방지용 스크롤 방지 해제
  const switchScroll = isOn => {
    const html = document.documentElement;
    const signPadScroll = document.getElementById('signPadScroll');

    // 스크롤 켜기
    if (isOn) {
      html.style.removeProperty('overflow');
      if (signPadScroll) signPadScroll.style.overflow = 'auto';
    }

    // 스크롤 끄기
    else {
      html.style.overflow = 'hidden';
      if (signPadScroll) signPadScroll.style.overflow = 'hidden';
    }
  };

  const fromDataURL = signData => {
    // 저장된 서명 or 성명 붙여넣기 실행
    canvas = canvasRef.current;
    context = canvas.getContext('2d');

    let image = new Image();
    image.src = signData;

    image.onload = () => {
      context.drawImage(image, 0, 0);
    };

    onChange(signData);
  };

  const getPosition = event => {
    const { clientX, clientY } = event;
    const x = canvasX(clientX);
    const y = canvasY(clientY);

    return { x, y };
  };

  // canvas에 선을 긋는 함수
  const drawLine = (originalMousePosition, newMousePosition) => {
    if (context) {
      context.beginPath();
      context.strokeStyle = 'black';
      context.lineWidth = 3;

      context.moveTo(originalMousePosition.x, originalMousePosition.y);
      context.lineTo(newMousePosition.x, newMousePosition.y);
      context.stroke();
      context.closePath();
    }
  };

  const startPaint = useCallback(event => {
    const position = getPosition(event);

    if (position) {
      isPainting = true;
      mousePosition = position;
    }
  }, []);

  const exitPaint = useCallback(() => {
    if (isPainting) {
      isPainting = false;
      onChange(canvas.toDataURL());
    }
  }, []);

  const paint = useCallback(event => {
    event.preventDefault();
    event.stopPropagation();

    if (isPainting) {
      const newMousePosition = getPosition(event);
      if (mousePosition && newMousePosition) {
        drawLine(mousePosition, newMousePosition);
        mousePosition = newMousePosition;
      }
    }
  }, []);

  const touchStartPaint = useCallback(event => {
    isPainting = true;
    const touches = event.changedTouches;

    const x = canvasX(touches[0].clientX);
    const y = canvasY(touches[0].clientY);

    context.beginPath();
    context.moveTo(x, y);

    switchScroll(false);
  }, []);

  const touchPaint = useCallback(event => {
    if (isPainting) {
      const touches = event.changedTouches;

      const x = canvasX(touches[0].clientX);
      const y = canvasY(touches[0].clientY);
      context.lineTo(x, y);

      context.strokeStyle = 'black';
      context.lineWidth = 3;
      context.stroke();
    }
  }, []);

  const touchExitPaint = useCallback(event => {
    switchScroll(true);

    if (isPainting) {
      event.preventDefault();
      context.closePath();
      isPainting = false;
      onChange(canvas.toDataURL());
    }
  }, []);

  const canvasX = clientX => {
    const bound = canvas.getBoundingClientRect();
    return (
      (clientX - bound.left - BW) * (canvas.width / (bound.width - BW * 2))
    );
  };

  const canvasY = clientY => {
    const bound = canvas.getBoundingClientRect();
    return (
      (clientY - bound.top - BW) * (canvas.height / (bound.height - BW * 2))
    );
  };
  // 서명패드 초기화
  const clearCanvas = () => {
    canvas = canvasRef.current;
    context = canvas.getContext('2d');
    context.clearRect(0, 0, canvas.width, canvas.height);
    onChange('');
  };

  const handleClearCanvas = () => {
    clearCanvas();

    if (pasteCheck) {
      onChangePasteCheck(false);
    }
    
    if (pasteCheck2) {
      onChangePasteCheck2(false);
    }
  };

  useEffect(() => {
    canvas = canvasRef.current;
    context = canvas.getContext('2d');

    canvas.addEventListener('mousedown', startPaint);
    canvas.addEventListener('mousemove', paint);
    canvas.addEventListener('mouseup', exitPaint);
    canvas.addEventListener('mouseleave', exitPaint);

    if (isMobile.any()) {
      canvas.addEventListener('touchstart', touchStartPaint);
      canvas.addEventListener('touchmove', touchPaint);
      canvas.addEventListener('touchend', touchExitPaint);
    }
    return () => {
      canvas.removeEventListener('mousedown', startPaint);
      canvas.removeEventListener('mousemove', paint);
      canvas.removeEventListener('mouseup', exitPaint);
      canvas.removeEventListener('mouseleave', exitPaint);

      if (isMobile.any()) {
        canvas.removeEventListener('touchstart', touchStartPaint);
        canvas.removeEventListener('touchmove', touchPaint);
        canvas.removeEventListener('touchend', touchExitPaint);
      }
    };
  }, [
    startPaint,
    paint,
    exitPaint,
    touchStartPaint,
    touchPaint,
    touchExitPaint,
  ]);

  return (
    <React.Fragment>
      <div className="tit-area">
        <h3>{title}</h3>
        <SatButton
          styleClass="btn-s2 b-blue side-r"
          label={'수정'}
          onClick={handleClearCanvas}
        />
      </div>

      <div className="sign-pad-area">
        <span>{name}</span>
        <div className="sign-pad">
          <canvas className="canvas" ref={canvasRef}></canvas>
        </div>
      </div>
      {error && <p className="text-info ico-type error">{error}</p>}
    </React.Fragment>
  );
}
